import { ApiClient } from '@api/api-configuration';
import { ContentType, type NoteUnreadCount } from '@api/READ_ONLY/client_api/Api';
import { skipGlobalErrorHandler } from '@context/api/ApiInterceptorProvider';

import {
  CreateNotePayload,
  Note,
  NoteFlagValue,
  NoteKind,
  NoteList,
  NoteListQuery,
  NoteRole,
  NOTE_KINDS,
  NOTE_ROLES,
  UpdateNotePayload,
} from './notes.types';

/**
 * The request layer for the notes endpoints.
 *
 * Every call goes through `ApiClient.<api>.request`, the generated client's own
 * escape hatch, rather than through the generated methods. Notes live under the
 * client in client-service (the client is the anchor) and the visit panel reads its
 * own list from visit-service.
 *
 * Every request is marked `skipGlobalErrorHandler`: a background poll for an unread
 * count must not put an error toast on a screen the user did not ask this of.
 */

type RawNote = Record<string, unknown>;

const asString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const asNumber = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/**
 * Reads a field under either spelling.
 *
 * The generated model is camelCase, but the table's columns are snake_case and a
 * serialiser that stopped aliasing them would empty the panel with no failed request.
 * Accepting both costs one lookup.
 */
const pick = (raw: RawNote, camel: string, snake: string): unknown => raw[camel] ?? raw[snake];

const isKnownRole = (value: unknown): value is NoteRole => (NOTE_ROLES as readonly string[]).includes(value as string);

/** One role code, or null when it is not one this app knows. */
const asRole = (value: unknown): NoteRole | null => (isKnownRole(value) ? value : null);

/**
 * The audience as the roles it reaches, or null when it could not be read whole.
 *
 * Null is not "no one". A note whose audience holds a code this app does not know may
 * well reach a doctor, and keeping the codes it does recognise would caption it as
 * office-only — a false claim about who has already read clinical content. An absent
 * or empty audience is the same kind of unknown: the column is NOT NULL with at least
 * one entry, so nothing legitimate arrives that way.
 */
const asAudience = (value: unknown): NoteRole[] | null => {
  if (!Array.isArray(value) || value.length === 0) return null;
  return value.every(isKnownRole) ? (value as NoteRole[]) : null;
};

const asKind = (value: unknown): NoteKind =>
  (NOTE_KINDS as readonly string[]).includes(value as string) ? (value as NoteKind) : 'VISIT_NOTE';

const asFlag = (value: unknown): NoteFlagValue | null =>
  value === 'ATTENTION' || value === 'INFO' || value === 'ROUTINE' ? value : null;

/**
 * One wire row as a `Note`, or null when the row carries no usable id.
 *
 * The id is the one field with no safe absence: it keys the row, it is what
 * `/notes/read` is posted, and it is how the visit link is built. A row given a
 * stand-in id would key every other unreadable row the same and would send that
 * stand-in to the server as though it were a note, so such a row is dropped instead.
 * The author's role and the audience are read the same way — unknown stays unknown.
 */
export const normalizeNote = (raw: RawNote): Note | null => {
  const noteId = asNumber(pick(raw, 'noteId', 'note_id'));
  if (noteId === null || !Number.isInteger(noteId) || noteId <= 0) return null;

  return {
    noteId,
    clientId: asNumber(pick(raw, 'clientId', 'client_id')) ?? 0,
    visitId: asNumber(pick(raw, 'visitId', 'visit_id')),
    kind: asKind(raw.kind),
    authorUserId: asNumber(pick(raw, 'authorUserId', 'author_user_id')),
    authorRole: asRole(pick(raw, 'authorRole', 'author_role')),
    authorName: asString(pick(raw, 'authorName', 'author_name')),
    audience: asAudience(raw.audience),
    body: asString(raw.body) ?? '',
    flag: asFlag(raw.flag),
    createdAt: asString(pick(raw, 'createdAt', 'created_at')) ?? '',
    updatedAt: asString(pick(raw, 'updatedAt', 'updated_at')),
    isRead: pick(raw, 'isRead', 'is_read') === true,
  };
};

const normalizeRows = (rows: readonly unknown[]): Note[] =>
  rows.map((entry) => normalizeNote(entry as RawNote)).filter((note): note is Note => note !== null);

/**
 * Notes out of a list response, whatever it wrapped them in.
 *
 * Both note endpoints answer with `PaginatedResponseNote`, but the services in this
 * repo return a bare array for some lists, and a reader that only knew one shape
 * would show an empty panel with no request having failed.
 */
const normalizeList = (payload: unknown): NoteList => {
  if (Array.isArray(payload)) {
    return { notes: normalizeRows(payload), total: payload.length };
  }

  if (payload && typeof payload === 'object') {
    const body = payload as Record<string, unknown>;
    const rows = body.data ?? body.notes ?? body.items;
    if (Array.isArray(rows)) {
      // `total` stays the server's number even when a row was dropped: it is the size
      // of the result set, and the panel compares it against the page it asked for.
      return { notes: normalizeRows(rows), total: asNumber(body.total) ?? rows.length };
    }
  }

  return { notes: [], total: 0 };
};

const listParams = (query: NoteListQuery): Record<string, unknown> => ({
  ...(query.visitId === undefined ? {} : { visit_id: query.visitId }),
  ...(query.kind === undefined ? {} : { kind: query.kind }),
  ...(query.flag === undefined ? {} : { flag: query.flag }),
  // One value, sent as a scalar. The endpoint's `author_role` is repeatable, and an
  // array here would be serialised as `author_role[]=cgv`, which FastAPI reads as no
  // filter at all - an unfiltered list presented as a filtered one.
  ...(query.authorRole === undefined ? {} : { author_role: query.authorRole }),
  ...(query.unreadOnly === undefined ? {} : { unread_only: query.unreadOnly }),
  ...(query.limit === undefined ? {} : { limit: query.limit }),
  ...(query.offset === undefined ? {} : { offset: query.offset }),
});

export const fetchClientNotes = async (clientId: number, query: NoteListQuery = {}): Promise<NoteList> => {
  const response = await ApiClient.clientApi.request<unknown>(
    skipGlobalErrorHandler({
      path: `/clients/${clientId}/notes`,
      method: 'GET',
      query: listParams(query),
      secure: true,
      format: 'json',
    })
  );

  return normalizeList(response.data);
};

export const fetchVisitNotes = async (visitId: number, query: NoteListQuery = {}): Promise<NoteList> => {
  const response = await ApiClient.visitsApi.request<unknown>(
    skipGlobalErrorHandler({
      path: `/visits/${visitId}/notes`,
      method: 'GET',
      query: listParams(query),
      secure: true,
      format: 'json',
    })
  );

  return normalizeList(response.data);
};

/**
 * Write a note. Null when the server answered with a row this app could not read —
 * the note exists, but nothing here may pretend to know which one it is.
 */
export const createClientNote = async (clientId: number, payload: CreateNotePayload): Promise<Note | null> => {
  const response = await ApiClient.clientApi.request<unknown>(
    skipGlobalErrorHandler({
      path: `/clients/${clientId}/notes`,
      method: 'POST',
      type: ContentType.Json,
      body: {
        visitId: payload.visitId ?? null,
        kind: payload.kind,
        audience: payload.audience,
        body: payload.body,
        flag: payload.flag ?? null,
      },
      secure: true,
      format: 'json',
    })
  );

  return normalizeNote((response.data ?? {}) as RawNote);
};

export const updateNote = async (noteId: number, payload: UpdateNotePayload): Promise<Note | null> => {
  const response = await ApiClient.clientApi.request<unknown>(
    skipGlobalErrorHandler({
      path: `/notes/${noteId}`,
      method: 'PATCH',
      type: ContentType.Json,
      body: payload,
      secure: true,
      format: 'json',
    })
  );

  return normalizeNote((response.data ?? {}) as RawNote);
};

export const deleteNote = async (noteId: number): Promise<void> => {
  await ApiClient.clientApi.request<unknown>(
    skipGlobalErrorHandler({
      path: `/notes/${noteId}`,
      method: 'DELETE',
      secure: true,
    })
  );
};

export const markNotesRead = async (noteIds: number[]): Promise<void> => {
  if (noteIds.length === 0) return;

  await ApiClient.clientApi.request<unknown>(
    skipGlobalErrorHandler({
      path: '/notes/read',
      method: 'POST',
      type: ContentType.Json,
      body: { noteIds },
      secure: true,
      format: 'json',
    })
  );
};

export const fetchUnreadCount = async (): Promise<NoteUnreadCount> => {
  const response = await ApiClient.clientApi.request<unknown>(
    skipGlobalErrorHandler({
      path: '/notes/unread-count',
      method: 'GET',
      secure: true,
      format: 'json',
    })
  );

  const body = (response.data ?? {}) as Record<string, unknown>;
  return { unread: asNumber(body.unread) ?? asNumber(body.count) ?? asNumber(body.total) ?? 0 };
};
